import { Link } from "react-router-dom";
import {
  KeyRound,
  ShieldCheck,
  Webhook,
  Lock,
  AlertTriangle,
  BookOpen,
  ArrowUpRight,
  Eye,
  Copy,
  Settings,
  RefreshCcw,
  CheckCircle2,
  PlayCircle,
} from "lucide-react";
import SEO from "@/components/SEO";
import DashboardHeader from "@/components/DashboardHeader";
import DashboardFooter from "@/components/DashboardFooter";
import { useOptionalMerchant } from "@/lib/useMerchant";
import BackToDashboard from "@/components/BackToDashboard";

/**
 * RazorpayKeysGuide.jsx
 * ------------------------------------------------------------------
 * Step-by-step walkthrough for a merchant to find their Razorpay
 * Key ID, Key Secret, and Webhook Secret, so they can paste them into
 * their Checkout DM payment settings.
 *
 * Text-only guide — no screenshots. The Webhook Secret section links
 * out to Razorpay's own webhooks documentation (nofollow) and embeds
 * a short walkthrough video near the end for merchants who'd rather
 * watch than read.
 * ------------------------------------------------------------------
 */

const ACCENTS = {
  lilac: { chip: "bg-lilac", chipText: "text-ink", soft: "bg-lilac/25" },
  mint: { chip: "bg-mint", chipText: "text-ink", soft: "bg-mint/25" },
  peach: { chip: "bg-peach", chipText: "text-ink", soft: "bg-peach/25" },
  hotpink: { chip: "bg-hotpink", chipText: "text-white", soft: "bg-hotpink/10" },
  ink: { chip: "bg-ink", chipText: "text-cream", soft: "bg-ink/[0.06]" },
};

function StepCard({ n, accent, title, desc, note }) {
  const a = ACCENTS[accent];
  return (
    <div className="relative rounded-2xl border border-ink/10 bg-white p-5">
      <div className="flex items-start gap-3.5">
        <span className={`grid h-10 w-10 flex-none place-items-center rounded-2xl ${a.chip} ${a.chipText} font-display text-sm font-black`}>
          {n}
        </span>
        <div className="min-w-0 flex-1">
          <p className="font-bold text-ink">{title}</p>
          <p className="mt-1.5 text-[13px] leading-relaxed text-ink/70">{desc}</p>
          {note && <p className="mt-2 text-[12px] leading-relaxed text-ink/45">{note}</p>}
        </div>
      </div>
    </div>
  );
}

function GuideSection({ id, icon: Icon, accent, name, tagline, what, children }) {
  const a = ACCENTS[accent];
  return (
    <section id={id} className="scroll-mt-24 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
      <div className="flex items-start gap-3.5">
        <span className={`grid h-12 w-12 flex-none place-items-center rounded-2xl ${a.chip} ${a.chipText}`}>
          <Icon size={20} strokeWidth={2.2} />
        </span>
        <div>
          <p className={`text-xs font-bold uppercase tracking-[0.2em] ${accent === "hotpink" ? "text-hotpink" : "text-ink/50"}`}>
            {tagline}
          </p>
          <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">{name}</h2>
        </div>
      </div>
      {what && <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">{what}</p>}
      <div className="mt-6 space-y-3">{children}</div>
    </section>
  );
}

export default function RazorpayKeysGuide() {
  const { me } = useOptionalMerchant();

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "TechArticle",
    headline: "Where to find your Razorpay API keys and webhook secret",
    description: "A step-by-step guide to locating your Razorpay Key ID, Key Secret, and Webhook Secret to connect to Checkout DM.",
    publisher: { "@type": "Organization", name: "Checkout DM", url: "https://checkoutdm.com" },
    mainEntityOfPage: "https://checkoutdm.com/razorpay-keys-guide",
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://checkoutdm.com/" },
      { "@type": "ListItem", position: 2, name: "Razorpay Keys Guide", item: "https://checkoutdm.com/razorpay-keys-guide" },
    ],
  };

  return (
    <div className="min-h-screen bg-cream">
      <SEO
        title="Find your Razorpay API Keys & Webhook Secret | Checkout DM"
        description="Step-by-step guide to locating your Razorpay Key ID, Key Secret, and Webhook Secret so you can connect Razorpay to Checkout DM."
        path="/razorpay-keys-guide"
        structuredData={[articleSchema, breadcrumbSchema]}
      />
      <DashboardHeader me={me} />

      <main className="mx-auto max-w-4xl px-6 pt-10 md:px-10">
        {me && <BackToDashboard />}

        <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Help · Razorpay</p>
        <h1 className="mt-2 font-display text-4xl font-black tracking-tighter md:text-5xl">
          Finding your Razorpay keys
        </h1>
        <p className="mt-3 max-w-2xl text-sm leading-relaxed text-ink/70 md:text-base">
          You'll need three things from your Razorpay Dashboard to connect Razorpay to
          Checkout DM: your <strong>Key ID</strong>, your <strong>Key Secret</strong>, and a{" "}
          <strong>Webhook Secret</strong>. This walks through exactly where each one lives.
        </p>

        <div className="mt-4 flex items-start gap-3 rounded-2xl border border-hotpink/20 bg-hotpink/5 p-4">
          <AlertTriangle size={16} className="mt-0.5 flex-none text-hotpink" />
          <p className="text-[13px] leading-relaxed text-ink/70">
            Your Key Secret and Webhook Secret are sensitive — treat them like a password.
            Razorpay only shows the Key Secret once, at the moment it's generated, so copy it
            somewhere safe (or straight into your Checkout DM profile) right away.
          </p>
        </div>

        <div className="mt-8 space-y-6">
          {/* API Keys */}
          <GuideSection
            id="api-keys"
            icon={KeyRound}
            accent="lilac"
            name="Key ID & Key Secret"
            tagline="Step 1"
            what="These two values authenticate every payment request Checkout DM sends on your behalf. You'll paste both into your merchant Profile in Checkout DM."
          >
            <StepCard
              n={1}
              accent="lilac"
              title="Log in to the Razorpay Dashboard"
              desc="Go to dashboard.razorpay.com and sign in with your Razorpay business account."
            />
            <StepCard
              n={2}
              accent="lilac"
              title="Pick Test Mode or Live Mode"
              desc="Use the toggle near the top of the dashboard to choose which mode you're generating keys for. Test mode keys are for trying things out with fake payments; Live mode keys are for real customer payments."
              note="Test and Live each have their own separate Key ID, Key Secret, and Webhook Secret — don't mix them up."
            />
            <StepCard
              n={3}
              accent="lilac"
              title="Open Account & Settings → API keys"
              desc="From the left-hand menu, go to Account & Settings, then find API keys under the 'Website and app settings' card."
            />
            <StepCard
              n={4}
              accent="lilac"
              title="Generate (or view) your keys"
              desc="If you haven't generated keys before, click Generate Key. Razorpay immediately shows your Key ID and Key Secret together, with a Download button — download or copy both now, since the Secret won't be shown in full again."
              note="Already have keys but lost the Secret? You'll need to click Regenerate Key, which creates a brand new pair — the old Key ID and Secret stop working immediately, so you'll need to update them anywhere they were in use, including Checkout DM."
            />

            <div className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-5">
              <p className="text-sm font-bold text-ink">Need a closer look at Live keys?</p>
              <p className="mt-2 text-[13px] leading-relaxed text-ink/70">
                Razorpay documents this same API keys screen — including the Live/Test toggle
                and where Live keys specifically live — in their own Dashboard guide.
              </p>
              <a
                href="https://razorpay.com/docs/payments/dashboard/account-settings/api-keys"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-hotpink hover:underline"
              >
                Razorpay's API keys documentation
                <ArrowUpRight size={14} strokeWidth={2.5} />
              </a>
              <div className="mt-5 aspect-video w-full overflow-hidden rounded-2xl border border-ink/10">
                <iframe
                  className="h-full w-full"
                  src="https://www.youtube.com/embed/Cer8UfBGX_E"
                  title="Finding your Razorpay Live keys"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </GuideSection>

          {/* Webhook */}
          <GuideSection
            id="webhook"
            icon={Webhook}
            accent="mint"
            name="Webhook Secret"
            tagline="Step 2"
            what="The webhook is how Razorpay tells Checkout DM the moment a payment succeeds or fails, in real time. The Webhook Secret lets Checkout DM verify that a webhook really came from Razorpay."
          >
            <StepCard
              n={1}
              accent="mint"
              title="Open Settings → Webhooks"
              desc="Still inside Account & Settings, find Webhooks in the left-hand menu, then click Add New Webhook."
            />

            <div className="rounded-2xl border border-mint/40 bg-mint/10 p-5">
              <p className="text-sm font-bold text-ink">Your Checkout DM webhook URL</p>
              <p className="mt-1.5 text-[13px] leading-relaxed text-ink/70">
                Paste this exact URL into the Webhook URL field in Razorpay — it's the same for
                every merchant, so you don't need to look it up anywhere else in your dashboard.
              </p>
              <div className="mt-3 flex items-center justify-between gap-3 rounded-xl border border-ink/10 bg-white px-4 py-3">
                <code className="min-w-0 flex-1 truncate text-[13px] font-semibold text-ink">
                  https://n8ncdm.checkoutdm.com/webhook/checkoutdm-razorpay-webhook
                </code>
                <Copy size={15} className="flex-none text-ink/40" />
              </div>
            </div>

            <StepCard
              n={2}
              accent="mint"
              title="Paste in the webhook URL and set a secret"
              desc="Paste the URL above into the URL field. Then choose a Secret — this is a value you type in yourself, Razorpay doesn't generate it for you."
              note="Use a long, random string for the secret. Whatever you type here is the exact value you'll paste into Checkout DM's Razorpay Webhook Secret field — they have to match exactly, or Checkout DM won't be able to verify incoming webhooks."
            />
            <StepCard
              n={3}
              accent="mint"
              title="Choose which events to subscribe to"
              desc="Below the URL and secret fields is a checklist of Active Events. This is where you tell Razorpay which payment events should actually trigger a call to your webhook URL."
              note={
                "At minimum, Checkout DM needs payment.captured and payment.failed. It's also worth ticking payment.authorized and order.paid if you see them, since they cover edge cases like delayed bank confirmations."
              }
            />
            <StepCard
              n={4}
              accent="mint"
              title="Save and confirm it's active"
              desc="Click Save Webhook. You can reopen it any time from your Webhooks list to confirm the URL, check which events are enabled, or change the secret."
            />

            <div className="rounded-2xl border border-ink/10 bg-ink/[0.02] p-5">
              <p className="text-sm font-bold text-ink">What each event actually means</p>
              <ul className="mt-3 space-y-2 text-[13px] leading-relaxed text-ink/70">
                <li>
                  <span className="font-semibold text-ink">payment.captured</span> — the payment
                  went through and the money is captured. This is the main event Checkout DM uses
                  to mark an order as paid.
                </li>
                <li>
                  <span className="font-semibold text-ink">payment.failed</span> — the payment
                  attempt failed. Checkout DM uses this to let the customer know and, where
                  possible, prompt them to retry.
                </li>
                <li>
                  <span className="font-semibold text-ink">payment.authorized</span> — the payment
                  was authorised by the bank but not yet captured. Useful for catching payments
                  that get confirmed a little later than usual, so nothing falls through the
                  cracks.
                </li>
                <li>
                  <span className="font-semibold text-ink">order.paid</span> — fires once an order
                  is fully paid, tying the payment back to the order it belongs to.
                </li>
              </ul>
              <p className="mt-4 text-[13px] leading-relaxed text-ink/70">
                Razorpay maintains the full, always-up-to-date list of webhook events — including
                ones for refunds, disputes, and subscriptions — in their own documentation. Worth
                a look if you want to go beyond what Checkout DM currently listens for.
              </p>
              <a
                href="https://razorpay.com/docs/webhooks"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 text-sm font-bold text-hotpink hover:underline"
              >
                Razorpay's Webhooks documentation
                <ArrowUpRight size={14} strokeWidth={2.5} />
              </a>
            </div>
          </GuideSection>

          {/* Video walkthrough */}
          <section className="scroll-mt-24 rounded-[2rem] border border-ink/10 bg-white p-6 md:p-8">
            <div className="flex items-start gap-3.5">
              <span className="grid h-12 w-12 flex-none place-items-center rounded-2xl bg-peach text-ink">
                <PlayCircle size={20} strokeWidth={2.2} />
              </span>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-ink/50">Prefer to watch?</p>
                <h2 className="mt-0.5 font-display text-2xl font-black tracking-tight md:text-3xl">
                  Video walkthrough
                </h2>
              </div>
            </div>
            <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ink/70">
              If you'd rather watch someone set up a Razorpay webhook end-to-end than follow the
              steps above, here's a short video covering the same ground.
            </p>
            <div className="mt-5 aspect-video w-full overflow-hidden rounded-2xl border border-ink/10">
              <iframe
                className="h-full w-full"
                src="https://www.youtube.com/embed/Xiikw4_CcQk"
                title="How to set up a Razorpay webhook"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </section>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-[2rem] border border-hotpink/20 bg-hotpink/5 p-6 md:p-8">
          <div className="flex items-start gap-3">
            <span className="grid h-10 w-10 flex-none place-items-center rounded-2xl bg-hotpink/10 text-hotpink">
              <ShieldCheck size={17} />
            </span>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-hotpink">Where these go</p>
              <p className="mt-1 max-w-md text-sm text-ink/70">
                Paste your Key ID, Key Secret, and Webhook Secret into the Razorpay section of your
                Payment Settings in Checkout DM. Nothing is charged until this is saved and verified.
              </p>
            </div>
          </div>
        </div>
      </main>

      <div className="mt-10">
        <DashboardFooter />
      </div>
    </div>
  );
}
